<template>
  <div class="lego-tree-table">
    <div class="lego-table" ref="legoTableRef">
      <!-- Table Control -->
      <div class="lego-table__control" v-if="!noControl && !childTable" :class="controlClass">
        <div class="lego-table__counter">
          <slot name="counter-instead">
            <span class="lego-table__counter__total"
              >{{ $_totalText }} <b>{{ pagination.totalItems }}</b></span
            >
            <lego-dropdown
              v-if="!noPage"
              class="lego-table__counter__per-page left-divider"
              @update:modelValue="
                v => {
                  $emit('change', v);
                }
              "
              :items="$_perPageItems"
              :height="26"
              v-model="rowPerPage"
            >
              <template v-slot:itemText="itemTextScope">
                <b>{{ itemTextScope.item && itemTextScope.item.text }}</b> {{ $_perPageText }}
              </template>
            </lego-dropdown>
          </slot>
        </div>
        <div class="lego-table__action">
          <div class="lego-table__action-selected" v-if="isActionSelect">
            <span>{{ selected.length }} {{ $_selectedText }}</span>
          </div>
          <slot name="action" />
        </div>
        <div class="lego-table__extra-action" v-if="$slots['extra-action']">
          <slot name="extra-action" />
        </div>
      </div>
      <div v-if="!childTable" class="header-table" :class="{ 'scroll-active': hasTableScroll }">
        <table
          class="lego-table__area"
          @mousemove="headerDragOver"
          @dragover="headerDragOver"
          @mouseup="headerDragEnd"
          @dragend="headerDragEnd"
          ref="cloneTable"
        >
          <!-- Header -->
          <thead class="lego-table__header" ref="tableHead" :class="headerClass">
            <slot v-if="customHeader" name="header" :attr="headerAttr" />
            <tr class="lego-table__header__row" v-else :style="{ height: headerHeight + 'px' }">
              <th
                v-for="(header, hIdx) in dumpHeaders"
                :key="hIdx"
                :style="thStyle(header)"
                :class="header.displayFrom ? 'lego-table__col__from-' + header.displayFrom : ''"
              >
                <span :style="{ 'line-height': headerContentHeight }">
                  {{ header.text }}
                  <lego-icon
                    v-if="header.sortable"
                    class="lego-table__icon-sort"
                    iconType="df"
                    :icon="sortIcon(hIdx)"
                    :size="16"
                    :class="{ 'lego-table__icon-sort--selected': sortIdx == hIdx }"
                    :style="sortIconStyle(hIdx)"
                    @click.stop="sortIconClick(hIdx)"
                  />
                  <lego-icon
                    v-if="header.filterable"
                    class="lego-table__icon-filter"
                    iconType="df"
                    icon="filter"
                    :size="16"
                    @click.stop="filterIconClick(hIdx)"
                  />
                </span>
                <lego-icon-wrapper class="lego-table__hidden-icon">
                  <lego-icon
                    icon-size="16px"
                    icon="change_width"
                    style="cursor: pointer;"
                    @click="header.displayFrom = ''"
                  />
                </lego-icon-wrapper>
                <div
                  class="lego-table__header-resize"
                  v-if="resizeColumn"
                  :style="headerResizeBoxStyle(hIdx)"
                  @dragstart="headerDragStart(hIdx, $event)"
                  @mousedown="headerDragStart(hIdx, $event)"
                />
              </th>
            </tr>
            <tr style="height: 0 !important; min-height: 0 !important;">
              <!--  -->
              <td class="lego-table__header__resizer-td">
                <div
                  v-if="resizeColumn"
                  v-show="toggleResize"
                  class="lego-table__resize-line"
                  :style="resizeLineStyle"
                />
              </td>
            </tr>
          </thead>
        </table>
      </div>
      <div
        class="lego-table__area-wrapper"
        :class="{ 'child-table': childTable }"
        ref="tableWrapper"
      >
        <table
          class="lego-table__area"
          @mousemove="headerDragOver"
          @dragover="headerDragOver"
          @mouseup="headerDragEnd"
          @dragend="headerDragEnd"
        >
          <tbody class="lego-table__body" ref="tableBody">
            <tr v-if="singleSelect && (multiSelect || selectAll)" class="lego-table__body__alert">
              <td :colspan="alertColspan">
                You can't use 'singleSelect prop' with multiSelect or selectAll prop
              </td>
            </tr>
            <tr v-else-if="items.length === 0" class="lego-table__body__no-data">
              <td :colspan="alertColspan">{{ $_noDataText }}</td>
            </tr>
            <template v-else v-for="(item, iIdx) in items" :key="item[itemId] + '_' + iIdx">
              <tr
                @click="onClickRow(item, 'row')"
                :style="{ height: tableRowHeight }"
                :id="item[itemId] + '_' + iIdx"
                :class="{
                  'lego-table__selected': isSelected(item),
                  sortableRow: rowSortable,
                  'lego-table__sorted': item.flag === 'E',
                  'row-hover': rowHover,
                  'has-children': item.children,
                  'children-opened': item.open,
                  depth_1: depth === 1,
                }"
              >
                <slot
                  name="items"
                  :item="item"
                  :index="iIdx"
                  :attr="itemsAttr(item)"
                  :children="item.children"
                >
                  <template v-for="(header, hIdx) in dumpHeaders">
                    <template v-if="!header.children">
                      <td
                        v-if="!header.checker"
                        :class="[
                          header.displayFrom && 'lego-table__col__from-' + header.displayFrom,
                        ]"
                        :key="item[itemId] + '_' + iIdx + '_' + hIdx"
                        :style="{
                          width: `${header.width}px`,
                          paddingLeft: `${hIdx === 0 ? (depth === 1 ? 16 : depth * 35) : 16}px`,
                        }"
                      >
                        <div class="td-inner">
                          <lego-icon
                            v-if="depth !== 1 && hIdx === 0 && item.children"
                            iconType="df"
                            icon="arrow_down"
                            :size="16"
                            sm
                          />
                          <v-runtime-template
                            v-if="header.customCell"
                            :template="header.customCell(item[header.value], iIdx)"
                          />
                          <div v-else class="value" v-lego-tooltip:ellipsis="item[header.value]">
                            {{ item[header.value] }}
                          </div>
                        </div>
                      </td>
                    </template>
                  </template>
                  <!-- Default Cell END -->
                </slot>
              </tr>
              <tr
                v-if="item.children && item.open"
                :key="item[itemId] + '_' + iIdx + '_children'"
                class="lego-table__row--children"
              >
                <td :colspan="alertColspan" class="lego-table__cell--children">
                  <lego-tree-table
                    :items="item.children"
                    :headers="headers"
                    :childTable="true"
                    :depth="depth + 1"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div v-if="customFooter" class="footer-table" :class="{ 'scroll-active': hasTableScroll }">
        <table class="lego-table__area">
          <tfoot class="lego-table__footer" ref="tableFoot">
            <slot name="footer" :attr="headerAttr" />
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  emits: ['change'],
  props: {
    childTable: {
      type: Boolean,
      default: false,
    },
    depth: {
      type: Number,
      default: 1,
    },
  },
  watch: {
    items() {
      this.openDepth1();
    },
  },
  mounted() {
    this.openDepth1();
  },
  methods: {
    toggleRow(item) {
      item.open = !item.open;
      this.$forceUpdate();
    },
    openDepth1() {
      if (this.depth === 1) {
        this.items.forEach(item => {
          if (item.children) {
            item.open = true;
          }
        });
        this.$forceUpdate();
      }
    },
    onClickRow(row, target) {
      if (target === 'row') {
        if(this.$attrs['onClickRow']) this.$attrs['onClickRow'](row);

        if (row.children) {
          this.toggleRow(row);
        }

        if (this.noClickRow) return;
      }

      if (target === 'checkbox') {
        this.onClickRowCheckbox(row);
      } else if (target === 'expand') {
        this.onClickRowExpand(row);
      } else {
        this.onClickRowCheckbox(row);
        this.onClickRowExpand(row);
      }
    },
  },
};
</script>
<style lang="scss">
.lego-tree-table {
  .lego-table__area {
    .lego-table__body {
      td {
        &.lego-table__cell--children {
          padding: 0;

          .lego-table {
            td:not(.lego-table__cell--children) {
              padding-left: 16px;
            }

            .lego-table__area-wrapper {
              border-top: 0;
              border-bottom: 0;
            }
          }
        }

        .td-inner {
          position: relative;
        }

        .opus-icon__arrow_down {
          position: absolute;
          top: 3px;
          left: -22px;
          font-size: 12px;
          margin-right: 10px;
          transform: rotate(-90deg);
          transition: all 0.1s;
          cursor: pointer;
        }
      }

      tr {
        &.children-opened {
          .opus-icon__arrow_down {
            transform: rotate(0deg);
          }
        }

        &.has-children {
          cursor: pointer;
        }

        &.depth_1 {
          cursor: default;
          td {
            color: $color--primary--tint-05;
          }
        }
      }
    }
  }
}
</style>
